import * as THREE from 'three';

import type { SatelliteKind } from '../data/types';

export const EARTH_SCENE_RADIUS = 1;
const EARTH_RADIUS_KM = 6378.137;

/** OrbitControls: stay outside Earth (radius 1), zoom out past GEO (~6.6 scene units). */
export const GLOBE_CAMERA_MIN_DISTANCE = 1.08;
/** ~80 scene units ≈ camera can frame orbits out to high MEO / deep-space catalog entries. */
export const GLOBE_CAMERA_MAX_DISTANCE = 80;
export const GLOBE_CAMERA_FAR = 1000;

export const SATELLITE_ICON_BASE = '/icons/satellites/';
export const SATELLITE_ICON_CANVAS_PX = 96;
export const SATELLITE_ICON_TINT_ALPHA = 0.18;

export const SATELLITE_KIND_DEFS: Record<
  SatelliteKind,
  { file: string; color: string }
> = {
  starlink: { file: 'starlink.svg', color: '#38bdf8' },
  station: { file: 'station.svg', color: '#fbbf24' },
  navigation: { file: 'navigation.svg', color: '#a78bfa' },
  agency: { file: 'agency.svg', color: '#4ade80' },
  military: { file: 'military.svg', color: '#fb923c' },
  geocomms: { file: 'geocomms.svg', color: '#22d3ee' },
  constellation: { file: 'constellation.svg', color: '#c4b5fd' },
  debris: { file: 'debris.svg', color: '#94a3b8' },
};

export const SATELLITE_KIND_COLORS: Record<SatelliteKind, number> = Object.fromEntries(
  Object.entries(SATELLITE_KIND_DEFS).map(([kind, def]) => [kind, parseInt(def.color.slice(1), 16)]),
) as Record<SatelliteKind, number>;

const svgCache = new Map<string, string>();
let dotTexture: THREE.CanvasTexture | null = null;

function normalizeSvg(svg: string): string {
  if (!/<svg\b/i.test(svg)) return svg;
  return svg.replace(/<svg\b([^>]*)>/i, (full, attrs) => {
    const w = /\bwidth=/i.test(attrs) ? '' : ' width="96"';
    const h = /\bheight=/i.test(attrs) ? '' : ' height="96"';
    return full.replace(attrs, `${attrs}${w}${h}`);
  });
}

async function fetchSvg(file: string): Promise<string> {
  const cached = svgCache.get(file);
  if (cached) return cached;
  const text = await fetch(`${SATELLITE_ICON_BASE}${file}?v=2`).then((r) => (r.ok ? r.text() : ''));
  if (text) svgCache.set(file, text);
  return text;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('satellite icon failed to load'));
    img.src = src;
  });
}

function fitImageRect(img: HTMLImageElement, size: number) {
  const iw = img.naturalWidth || size;
  const ih = img.naturalHeight || size;
  const scale = Math.min(size / iw, size / ih);
  const w = iw * scale;
  const h = ih * scale;
  return { x: (size - w) / 2, y: (size - h) / 2, w, h };
}

async function svgToImageData(kind: SatelliteKind): Promise<ImageData | null> {
  const def = SATELLITE_KIND_DEFS[kind];
  const svg = await fetchSvg(def.file);
  if (!svg) return null;
  const blob = new Blob([normalizeSvg(svg)], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = SATELLITE_ICON_CANVAS_PX;
    canvas.height = SATELLITE_ICON_CANVAS_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const { x, y, w, h } = fitImageRect(img, SATELLITE_ICON_CANVAS_PX);
    ctx.clearRect(0, 0, SATELLITE_ICON_CANVAS_PX, SATELLITE_ICON_CANVAS_PX);
    ctx.drawImage(img, x, y, w, h);
    if (SATELLITE_ICON_TINT_ALPHA > 0) {
      ctx.globalCompositeOperation = 'source-atop';
      ctx.fillStyle = def.color;
      ctx.globalAlpha = SATELLITE_ICON_TINT_ALPHA;
      ctx.fillRect(0, 0, SATELLITE_ICON_CANVAS_PX, SATELLITE_ICON_CANVAS_PX);
    }
    return ctx.getImageData(0, 0, SATELLITE_ICON_CANVAS_PX, SATELLITE_ICON_CANVAS_PX);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function imageDataToTexture(data: ImageData): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = data.width;
  canvas.height = data.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.putImageData(data, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** Canvas lat/lon grid shown until the night map loads (reference wT). */
export function createEarthGridFallbackTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');

  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#0c1929');
  gradient.addColorStop(0.45, '#0f2744');
  gradient.addColorStop(0.55, '#143352');
  gradient.addColorStop(1, '#0a1628');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = 'rgba(56, 189, 248, 0.08)';
  ctx.lineWidth = 1;
  for (let lat = -80; lat <= 80; lat += 20) {
    const y = ((90 - lat) / 180) * canvas.height;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(canvas.width, y);
    ctx.stroke();
  }
  for (let lon = -180; lon < 180; lon += 30) {
    const x = ((lon + 180) / 360) * canvas.width;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvas.height);
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export function latLonAltKmToScene(lat: number, lon: number, altKm: number): THREE.Vector3 {
  const r = EARTH_SCENE_RADIUS + altKm / EARTH_RADIUS_KM;
  const phi = ((90 - lat) * Math.PI) / 180;
  const lambda = ((lon + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(lambda),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(lambda),
  );
}

export interface EarthGlobe {
  group: THREE.Group;
  earth: THREE.Mesh;
  atmosphere: THREE.Mesh;
  grid: THREE.LineSegments;
  dispose: () => void;
}

/** Earth sphere, atmosphere shell, and 3D lat/lon grid (reference AT). */
export function createEarthGlobe(): EarthGlobe {
  const group = new THREE.Group();
  let disposed = false;

  const geometry = new THREE.SphereGeometry(EARTH_SCENE_RADIUS, 64, 32);
  const loader = new THREE.TextureLoader();
  let map: THREE.Texture = createEarthGridFallbackTexture();

  loader.load(
    '/textures/earth/night.jpg',
    (night) => {
      if (disposed) {
        night.dispose();
        return;
      }
      night.colorSpace = THREE.SRGBColorSpace;
      map.dispose();
      map = night;
      earth.material.map = night;
      earth.material.needsUpdate = true;
    },
    undefined,
    () => {},
  );

  const material = new THREE.MeshPhongMaterial({
    map,
    specular: new THREE.Color(0x050505),
    shininess: 4,
  });
  const earth = new THREE.Mesh(geometry, material);
  group.add(earth);

  const atmosphere = new THREE.Mesh(
    new THREE.SphereGeometry(EARTH_SCENE_RADIUS * 1.02, 64, 32),
    new THREE.MeshBasicMaterial({
      color: 0x38bdf8,
      transparent: true,
      opacity: 0.12,
      side: THREE.BackSide,
      depthWrite: false,
    }),
  );
  group.add(atmosphere);

  const gridVerts: number[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const gridAltKm = 2;

  for (let lat = -80; lat <= 80; lat += 20) {
    for (let lon = -180; lon < 180; lon += 6) {
      a.copy(latLonAltKmToScene(lat, lon, gridAltKm));
      b.copy(latLonAltKmToScene(lat, lon + 6, gridAltKm));
      gridVerts.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
  }
  for (let lon = -180; lon < 180; lon += 30) {
    for (let lat = -80; lat < 80; lat += 6) {
      a.copy(latLonAltKmToScene(lat, lon, gridAltKm));
      b.copy(latLonAltKmToScene(lat + 6, lon, gridAltKm));
      gridVerts.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
  }

  const gridGeo = new THREE.BufferGeometry();
  gridGeo.setAttribute('position', new THREE.Float32BufferAttribute(gridVerts, 3));
  const grid = new THREE.LineSegments(
    gridGeo,
    new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.15 }),
  );
  group.add(grid);

  return {
    group,
    earth,
    atmosphere,
    grid,
    dispose: () => {
      disposed = true;
      geometry.dispose();
      material.dispose();
      map.dispose();
      atmosphere.geometry.dispose();
      (atmosphere.material as THREE.Material).dispose();
      gridGeo.dispose();
      (grid.material as THREE.Material).dispose();
    },
  };
}

/** Soft circular sprite for bulk satellite points (reference JT). */
export function createSatelliteDotTexture(): THREE.CanvasTexture {
  if (dotTexture) return dotTexture;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.55, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  dotTexture = new THREE.CanvasTexture(canvas);
  dotTexture.needsUpdate = true;
  return dotTexture;
}

export interface SatelliteIconAtlas {
  textures: Map<SatelliteKind, THREE.Texture>;
  dispose: () => void;
}

export async function loadSatelliteIconAtlas(): Promise<SatelliteIconAtlas> {
  const textures = new Map<SatelliteKind, THREE.Texture>();
  await Promise.all(
    (Object.keys(SATELLITE_KIND_DEFS) as SatelliteKind[]).map(async (kind) => {
      const data = await svgToImageData(kind);
      if (data) textures.set(kind, imageDataToTexture(data));
    }),
  );
  return {
    textures,
    dispose: () => {
      for (const texture of textures.values()) texture.dispose();
      textures.clear();
    },
  };
}

export function createSelectedSatelliteSprite(
  atlas: SatelliteIconAtlas,
  kind: SatelliteKind,
  scale = 0.09,
): THREE.Sprite {
  const map = atlas.textures.get(kind) ?? atlas.textures.get('debris');
  const material = new THREE.SpriteMaterial({
    map,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const sprite = new THREE.Sprite(material);
  sprite.scale.setScalar(scale);
  return sprite;
}

export function disposeSelectedSatelliteSprite(sprite: THREE.Sprite | null) {
  if (!sprite) return;
  sprite.material.map?.dispose();
  sprite.material.dispose();
}
